import { useState } from 'react';
import { api } from '../../api/client.js';
import {
  Alert,
  Field,
  Notice,
  PageHeader,
  datum,
  eur,
  fehlertext,
  useLoad,
} from '../shared/ui.js';

interface PriceList {
  priceListId: string;
  name: string;
  currency: string;
  activeVersion: {
    id: string;
    versionLabel: string;
    validFrom: string | null;
    itemCount: number;
  } | null;
  versionCount: number;
}
interface Version {
  id: string;
  versionLabel: string;
  status: string;
  validFrom: string | null;
  itemCount: number;
  importSource: string | null;
  importedAt: string | null;
  activatedAt: string | null;
  note: string | null;
}
interface Preview {
  rowCount: number;
  blockingErrors: Array<{ rowNumber?: number; message: string }>;
  preview: {
    recognizedRows: number;
    changedPrices: number;
    unchangedPrices: number;
    newArticles: number;
    unknownArticleNumbers: number;
    averageChangePercent: string | null;
    medianChangePercent: string | null;
    largestIncreases: Array<{
      articleNumber: string;
      oldPrice: string;
      newPrice: string;
      changePercent: string;
    }>;
    largestDecreases: Array<{
      articleNumber: string;
      oldPrice: string;
      newPrice: string;
      changePercent: string;
    }>;
    warnings: Array<{ code: string; message: string; count?: number }>;
  };
}

/** Preislistenverwaltung: Versionen, Upload mit Vorschau, Preisaenderung, Einzelpreise. Nur Betreiber. */
export function PriceListsPage(): React.JSX.Element {
  const lists = useLoad<{ items: PriceList[] }>('/api/admin/pricelists');
  const pl = lists.data?.items[0] ?? null;
  const versions = useLoad<{ items: Version[] }>(
    pl ? `/api/pricelists/${pl.priceListId}/versions` : null,
    [pl?.priceListId],
  );
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<'versionen' | 'upload' | 'anpassung' | 'einzel'>(
    'versionen',
  );

  async function aktivieren(v: Version): Promise<void> {
    if (
      !window.confirm(
        `Version ${v.versionLabel} aktivieren? Die bisher aktive Version wird archiviert. Neue Angebote rechnen ab sofort mit diesem Stand.`,
      )
    )
      return;
    setErr(null);
    try {
      await api.post(`/api/pricelists/versions/${v.id}/activate`);
      versions.reload();
      lists.reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  async function validFrom(v: Version): Promise<void> {
    const d = window.prompt(
      'Gueltig ab (JJJJ-MM-TT):',
      new Date().toISOString().slice(0, 10),
    );
    if (!d) return;
    // Gueltigkeitsdatum ist Voraussetzung fuer die Aktivierung (active_needs_valid_from).
    try {
      await api.patch(`/api/admin/pricelists/versions/${v.id}`, { validFrom: d });
      versions.reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <>
      <PageHeader
        title="Preislisten"
        lead={
          pl
            ? `${pl.name} · aktiv: ${pl.activeVersion?.versionLabel ?? 'keine'} (${pl.activeVersion?.itemCount ?? 0} Preise)`
            : undefined
        }
      />
      <Alert text={lists.error ?? err} />
      <nav className="tabs">
        {(['versionen', 'upload', 'anpassung', 'einzel'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={tab === t ? 'tab selected' : 'tab'}
            onClick={() => setTab(t)}
          >
            {
              {
                versionen: 'Versionen',
                upload: 'Datei importieren',
                anpassung: 'Preisaenderung',
                einzel: 'Einzelpreise',
              }[t]
            }
          </button>
        ))}
      </nav>
      {pl && tab === 'versionen' && (
        <div className="card">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Gueltig ab</th>
                  <th className="num">Preise</th>
                  <th>Herkunft</th>
                  <th>Notiz</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(versions.data?.items ?? []).map((v) => (
                  <tr key={v.id}>
                    <td>
                      <strong>{v.versionLabel}</strong>
                    </td>
                    <td>
                      {v.status === 'active' ? (
                        <span className="pill pill-ok">aktiv</span>
                      ) : v.status === 'draft' ? (
                        <span className="pill pill-warn">Entwurf</span>
                      ) : (
                        <span className="pill pill-off">archiviert</span>
                      )}
                    </td>
                    <td>
                      {v.validFrom ? (
                        datum(v.validFrom)
                      ) : (
                        <span className="muted">fehlt</span>
                      )}
                    </td>
                    <td className="num">{v.itemCount}</td>
                    <td>{v.importSource ?? '–'}</td>
                    <td className="note">{v.note ?? ''}</td>
                    <td className="row">
                      {v.status === 'draft' && !v.validFrom && (
                        <button
                          type="button"
                          className="link"
                          onClick={() => void validFrom(v)}
                        >
                          Gueltigkeit setzen
                        </button>
                      )}
                      {v.status === 'draft' && v.validFrom && (
                        <button
                          type="button"
                          className="primary"
                          onClick={() => void aktivieren(v)}
                        >
                          Aktivieren
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {pl && tab === 'upload' && (
        <Upload
          priceListId={pl.priceListId}
          onDone={() => {
            versions.reload();
            setTab('versionen');
          }}
        />
      )}
      {pl && tab === 'anpassung' && (
        <Anpassung
          priceListId={pl.priceListId}
          onDone={() => {
            versions.reload();
            setTab('versionen');
          }}
        />
      )}
      {pl && tab === 'einzel' && (
        <Einzelpreise
          priceListId={pl.priceListId}
          onDone={() => {
            versions.reload();
            setTab('versionen');
          }}
        />
      )}
    </>
  );
}

function VorschauBlock({ p }: { p: Preview }): React.JSX.Element {
  return (
    <>
      {p.blockingErrors.length > 0 && (
        <Notice>
          <strong>{p.blockingErrors.length} blockierende Fehler</strong>
          <ul>
            {p.blockingErrors.slice(0, 10).map((e, i) => (
              <li key={i}>
                {e.rowNumber ? `Zeile ${e.rowNumber}: ` : ''}
                {e.message}
              </li>
            ))}
          </ul>
        </Notice>
      )}
      <div className="kpis">
        <div className="kpi">
          <span className="kpi-label">Zeilen</span>
          <span className="kpi-value">{p.rowCount}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Neue Preise</span>
          <span className="kpi-value">{p.preview.newArticles}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Geaenderte</span>
          <span className="kpi-value">{p.preview.changedPrices}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Unveraenderte</span>
          <span className="kpi-value">{p.preview.unchangedPrices}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Unbekannte Artikel</span>
          <span
            className={
              p.preview.unknownArticleNumbers > 0 ? 'kpi-value kpi-warn' : 'kpi-value'
            }
          >
            {p.preview.unknownArticleNumbers}
          </span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Median Aenderung</span>
          <span className="kpi-value">{p.preview.medianChangePercent ?? '–'} %</span>
        </div>
      </div>
      {p.preview.warnings.length > 0 && (
        <ul className="note">
          {p.preview.warnings.map((w, i) => (
            <li key={i}>{w.message}</li>
          ))}
        </ul>
      )}
      {p.preview.largestIncreases.length > 0 && (
        <details>
          <summary>Groesste Erhoehungen</summary>
          <table>
            <tbody>
              {p.preview.largestIncreases.map((c) => (
                <tr key={c.articleNumber}>
                  <td>
                    <code>{c.articleNumber}</code>
                  </td>
                  <td className="num">{eur(c.oldPrice)}</td>
                  <td className="num">→ {eur(c.newPrice)}</td>
                  <td className="num">{c.changePercent} %</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </>
  );
}

function Upload({
  priceListId,
  onDone,
}: {
  priceListId: string;
  onDone: () => void;
}): React.JSX.Element {
  const [datei, setDatei] = useState<File | null>(null);
  const [f, setF] = useState({
    versionLabel: '',
    mode: 'full',
    delimiter: ';',
    decimalSeparator: ',',
    articleNumberColumn: 'articleNumber',
    listPriceColumn: 'listPrice',
    note: '',
  });
  const [vorschau, setVorschau] = useState<Preview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = () => {
    const fd = new FormData();
    if (datei) fd.append('file', datei);
    fd.append('priceListId', priceListId);
    for (const [k, v] of Object.entries(f)) if (v !== '') fd.append(k, v);
    return fd;
  };

  async function pruefen(): Promise<void> {
    setErr(null);
    setBusy(true);
    try {
      setVorschau(
        await api.upload<Preview>('/api/admin/pricelists/upload-preview', form()),
      );
    } catch (x) {
      setErr(fehlertext(x));
    } finally {
      setBusy(false);
    }
  }
  async function importieren(): Promise<void> {
    setErr(null);
    setBusy(true);
    try {
      await api.upload('/api/admin/pricelists/upload-import', form());
      onDone();
    } catch (x) {
      setErr(fehlertext(x));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3>Preisliste importieren</h3>
      <p className="note">
        Es entsteht immer ein Entwurf. Aktiviert wird getrennt, nach Pruefung. Unbekannte
        Artikelnummern blockieren die Aktivierung, nicht den Import.
      </p>
      <div className="grid2">
        <Field label="CSV-Datei">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setDatei(e.target.files?.[0] ?? null)}
          />
        </Field>
        <Field label="Versionsbezeichnung">
          <input
            value={f.versionLabel}
            onChange={(e) => setF({ ...f, versionLabel: e.target.value })}
            placeholder="z. B. 2026-10"
          />
        </Field>
        <Field label="Modus">
          <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
            <option value="full">Vollimport</option>
            <option value="delta">
              Delta (fehlende Artikel behalten den bisherigen Preis)
            </option>
          </select>
        </Field>
        <Field label="Trennzeichen">
          <select
            value={f.delimiter}
            onChange={(e) => setF({ ...f, delimiter: e.target.value })}
          >
            <option value=";">Semikolon</option>
            <option value=",">Komma</option>
            <option value="	">Tabulator</option>
          </select>
        </Field>
        <Field label="Dezimaltrennzeichen">
          <select
            value={f.decimalSeparator}
            onChange={(e) => setF({ ...f, decimalSeparator: e.target.value })}
          >
            <option value=",">Komma</option>
            <option value=".">Punkt</option>
          </select>
        </Field>
        <Field label="Spalte Artikelnummer">
          <input
            value={f.articleNumberColumn}
            onChange={(e) => setF({ ...f, articleNumberColumn: e.target.value })}
          />
        </Field>
        <Field label="Spalte Listenpreis">
          <input
            value={f.listPriceColumn}
            onChange={(e) => setF({ ...f, listPriceColumn: e.target.value })}
          />
        </Field>
        <Field label="Notiz">
          <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
      </div>
      <Alert text={err} />
      <div className="row">
        <button type="button" disabled={!datei || busy} onClick={() => void pruefen()}>
          Vorschau pruefen
        </button>
        <button
          type="button"
          className="primary"
          disabled={
            !datei ||
            !f.versionLabel ||
            busy ||
            (vorschau?.blockingErrors.length ?? 0) > 0
          }
          onClick={() => void importieren()}
        >
          Als Entwurf importieren
        </button>
      </div>
      {vorschau && <VorschauBlock p={vorschau} />}
    </section>
  );
}

function Anpassung({
  priceListId,
  onDone,
}: {
  priceListId: string;
  onDone: () => void;
}): React.JSX.Element {
  const [f, setF] = useState({
    changePercent: '4',
    versionLabel: '',
    productTypeCode: '',
    materialCode: '',
    note: '',
  });
  const [vorschau, setVorschau] = useState<{
    rows: Array<{
      articleNumber: string;
      description: string;
      oldPrice: string;
      newPrice: string;
      changePercent: string;
    }>;
    affected: number;
    unchanged: number;
    totalRows: number;
    truncated: boolean;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const koerper = () => ({
    priceListId,
    changePercent: Number(f.changePercent),
    ...(f.productTypeCode ? { productTypeCode: f.productTypeCode } : {}),
    ...(f.materialCode ? { materialCode: f.materialCode } : {}),
  });

  async function pruefen(): Promise<void> {
    setErr(null);
    try {
      setVorschau(await api.post('/api/admin/pricelists/adjustment/preview', koerper()));
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  async function anwenden(): Promise<void> {
    if (
      !window.confirm(
        `Neuen Entwurf mit ${f.changePercent} % auf ${vorschau?.affected ?? '?'} Artikel erzeugen? Die aktive Version bleibt unveraendert, bis der Entwurf aktiviert wird.`,
      )
    )
      return;
    setErr(null);
    try {
      await api.post('/api/admin/pricelists/adjustment', {
        ...koerper(),
        versionLabel: f.versionLabel,
        note: f.note || undefined,
      });
      onDone();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  return (
    <section className="card">
      <h3>Globale Preisaenderung</h3>
      <p className="note">
        Kopiert die aktive Version als neuen Entwurf und aendert alle betroffenen Preise
        um den Prozentsatz. Rundung auf Cent. Nie in-place.
      </p>
      <div className="grid2">
        <Field label="Aenderung in %" hint="Positiv erhoeht, negativ senkt.">
          <input
            type="number"
            step="0.1"
            value={f.changePercent}
            onChange={(e) => setF({ ...f, changePercent: e.target.value })}
          />
        </Field>
        <Field label="Versionsbezeichnung">
          <input
            value={f.versionLabel}
            onChange={(e) => setF({ ...f, versionLabel: e.target.value })}
            placeholder="z. B. 2027-01 (+4 %)"
          />
        </Field>
        <Field label="Nur Produkttyp (Code)" hint="Leer: alle.">
          <input
            value={f.productTypeCode}
            onChange={(e) => setF({ ...f, productTypeCode: e.target.value })}
            maxLength={4}
          />
        </Field>
        <Field label="Nur Werkstoff (Code)" hint="Leer: alle.">
          <input
            value={f.materialCode}
            onChange={(e) => setF({ ...f, materialCode: e.target.value })}
            maxLength={4}
          />
        </Field>
      </div>
      <Alert text={err} />
      <div className="row">
        <button type="button" onClick={() => void pruefen()}>
          Vorschau
        </button>
        <button
          type="button"
          className="primary"
          disabled={!vorschau || !f.versionLabel}
          onClick={() => void anwenden()}
        >
          Entwurf erzeugen
        </button>
      </div>
      {vorschau && (
        <>
          <p>
            <strong>{vorschau.affected}</strong> Artikel betroffen, {vorschau.unchanged}{' '}
            unveraendert von {vorschau.totalRows}.
            {vorschau.truncated && ' Vorschau zeigt die ersten 200.'}
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Bezeichnung</th>
                  <th className="num">bisher</th>
                  <th className="num">neu</th>
                  <th className="num">Aenderung</th>
                </tr>
              </thead>
              <tbody>
                {vorschau.rows.slice(0, 50).map((r) => (
                  <tr key={r.articleNumber}>
                    <td>
                      <code>{r.articleNumber}</code>
                    </td>
                    <td>{r.description}</td>
                    <td className="num">{eur(r.oldPrice)}</td>
                    <td className="num">{eur(r.newPrice)}</td>
                    <td className="num">{r.changePercent} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Einzelpreise({
  priceListId,
  onDone,
}: {
  priceListId: string;
  onDone: () => void;
}): React.JSX.Element {
  const [zeilen, setZeilen] = useState([{ articleNumber: '', listPrice: '' }]);
  const [versionLabel, setVersionLabel] = useState('');
  const [err, setErr] = useState<string | null>(null);
  async function speichern(): Promise<void> {
    setErr(null);
    try {
      await api.post(`/api/admin/pricelists/${priceListId}/price-edit`, {
        versionLabel,
        items: zeilen
          .filter((z) => z.articleNumber)
          .map((z) => ({
            articleNumber: z.articleNumber,
            listPrice: z.listPrice === '' ? null : z.listPrice.replace(',', '.'),
          })),
      });
      onDone();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  return (
    <section className="card">
      <h3>Einzelpreise pflegen</h3>
      <p className="note">
        Erzeugt einen neuen Entwurf auf Basis der aktiven Version mit den geaenderten
        Zeilen. Leerer Preis entfernt den Artikel aus dem Preisstand. Neue Artikel muessen
        im Artikelstamm existieren.
      </p>
      <table>
        <thead>
          <tr>
            <th>Artikelnummer</th>
            <th>Listenpreis (leer = entfernen)</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z, i) => (
            <tr key={i}>
              <td>
                <input
                  value={z.articleNumber}
                  onChange={(e) =>
                    setZeilen(
                      zeilen.map((x, j) =>
                        j === i ? { ...x, articleNumber: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <input
                  value={z.listPrice}
                  placeholder="z. B. 4,20"
                  onChange={(e) =>
                    setZeilen(
                      zeilen.map((x, j) =>
                        j === i ? { ...x, listPrice: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <button
                  type="button"
                  className="link"
                  onClick={() => setZeilen(zeilen.filter((_, j) => j !== i))}
                >
                  entfernen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row">
        <button
          type="button"
          onClick={() => setZeilen([...zeilen, { articleNumber: '', listPrice: '' }])}
        >
          Zeile hinzufuegen
        </button>
      </div>
      <Field label="Versionsbezeichnung">
        <input
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          placeholder="z. B. 2026-06a"
        />
      </Field>
      <Alert text={err} />
      <div className="row">
        <button
          type="button"
          className="primary"
          disabled={!versionLabel}
          onClick={() => void speichern()}
        >
          Entwurf erzeugen
        </button>
      </div>
    </section>
  );
}
